import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import {
    parameterizableSecretBaseToModel,
    parameterizablePasswordOrAKVtoSecretBase,
    passwordOrAKVtoSecretBase,
    secretBaseToPasswordOrAKV
} from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { restAuthRefTypes, RestServiceLinkedService } from 'app/model/linkedservice/rest-service.linkedservice';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';
import { KeyValuePairWithAkv, Owned, ReferenceAdapter, Credential, restAuthTypes, CredentialTypeOptions, azureCloudTypeDef, servicePrincipalCredentialTypes } from 'shell-models-lib';
import { GlobalVariables } from 'app/services/globals';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(RestServiceLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                url: '*',
                enableServerCertificateValidation: '*',
                authenticationType: '*',
                userName: '*',
                password: 'secret',
                servicePrincipalId: '*',
                servicePrincipalCredentialType: '*',
                servicePrincipalKey: 'secret',
                servicePrincipalEmbeddedCert: 'secret',
                servicePrincipalEmbeddedCertPassword: 'secret',
                tenant: '*',
                aadResourceId: '*',
                encryptedCredential: '*',
                azureCloudType: '*',
                authHeaders: 'secret',
                clientId: '*',
                clientSecret: 'secret',
                tokenEndpoint: '*',
                scope: '*',
                resource: '*',
                credential: '*'
            },
            parameters: '*'
        }
    }
})
export class RestServiceLinkedServiceAdapter extends LinkedServiceAdapter<RestServiceLinkedService> {
    protected supportFirstClassParameterExperience = true;

    /**
     * MI and UAMI share the same auth type value in the payload so we need to update authenticationType based on fetched credential type,
     * @param model linked service model
     */
    public async postToModel(model: RestServiceLinkedService): Promise<void> {
        let finalAuthType: string = model.authenticationType;
        if (model.credential) {
            const credType = model.credential.getType();
            if (credType === CredentialTypeOptions.ManagedIdentity) {
                finalAuthType = restAuthTypes.ManagedServiceIdentity;
            } else if (credType === CredentialTypeOptions.UserAssignedManagedIdentity) {
                finalAuthType = restAuthTypes.UserAssignedManagedIdentity;
            }
        }
        model.authenticationType = finalAuthType;
    }

    protected modelConstructor(): new () => RestServiceLinkedService {
        return RestServiceLinkedService;
    }

    protected model(): IModel {
        return RestServiceLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: RestServiceLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.RestLinkedService;
        properties.type = Metamodel.modelFor(RestServiceLinkedService).name;
        let userName: string;
        let password: string | Model.SecretBase;
        let servicePrincipalId: string;
        let servicePrincipalKey: string | Model.SecretBase;
        let servicePrincipalCredentialType: string;
        let servicePrincipalEmbeddedCert: string | Model.SecretBase;
        let servicePrincipalEmbeddedCertPassword: string | Model.SecretBase;
        let aadResourceId: string;
        let tenant: string;
        let azureCloudType: azureCloudTypeDef;
        let clientId: string;
        let clientSecret: string | Model.SecretBase;
        let tokenEndpoint: string;
        let scope: string;
        let resource: string;
        let credential: Model.CredentialReference;
        let authenticationType = linkedService.authenticationType;

        if (linkedService.authenticationType === restAuthTypes.Basic) {
            userName = this.valueToResource(linkedService, RestServiceLinkedService.USER_NAME);
            password = parameterizablePasswordOrAKVtoSecretBase(linkedService, RestServiceLinkedService.PASSWORD, this.context);
        } else if (linkedService.authenticationType === restAuthTypes.AadServicePrincipal) {
            if (linkedService.authReferenceMethod === restAuthRefTypes.InlineRefTypeAuthentication) {
                servicePrincipalId = this.valueToResource(linkedService, RestServiceLinkedService.SERVICE_PRINCIPAL_ID);
                servicePrincipalCredentialType = !linkedService.isOldServicePrincipalResource ? linkedService.servicePrincipalCredentialType : undefined;
                if (servicePrincipalCredentialType === servicePrincipalCredentialTypes.ServicePrincipalCert) {
                    servicePrincipalEmbeddedCert = parameterizablePasswordOrAKVtoSecretBase(linkedService, RestServiceLinkedService.SERVICE_PRINCIPAL_EMBEDDED_CERT, this.context);
                    servicePrincipalEmbeddedCertPassword = parameterizablePasswordOrAKVtoSecretBase(linkedService, RestServiceLinkedService.SERVICE_PRINCIPAL_EMBEDDED_CERT_PASSWORD, this.context);
                } else {
                    // This is for old existing linked services with sp auth
                    servicePrincipalKey = parameterizablePasswordOrAKVtoSecretBase(linkedService, RestServiceLinkedService.SERVICE_PRINCIPAL_KEY, this.context);
                }

                tenant = this.valueToResource(linkedService, RestServiceLinkedService.TENANT);
                azureCloudType = this.valueToResource(linkedService, RestServiceLinkedService.AZURE_CLOUD_TYPE);
            } else {
                credential = (linkedService.credential) ?
                    ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference : undefined;
            }
            aadResourceId = this.valueToResource(linkedService, RestServiceLinkedService.AAD_RESOURCE_ID);
        } else if (linkedService.authenticationType === restAuthTypes.ManagedServiceIdentity || linkedService.authenticationType === restAuthTypes.UserAssignedManagedIdentity) {
            authenticationType = restAuthTypes.ManagedServiceIdentity;
            aadResourceId = this.valueToResource(linkedService, RestServiceLinkedService.AAD_RESOURCE_ID);
            credential = (linkedService.credential) ?
                ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference : undefined;
        } else if (linkedService.authenticationType === restAuthTypes.OAuth2ClientCredential) {
            clientId = this.valueToResource(linkedService, RestServiceLinkedService.CLIENT_ID);
            clientSecret = parameterizablePasswordOrAKVtoSecretBase(linkedService, RestServiceLinkedService.CLIENT_SECRET, this.context);
            tokenEndpoint = this.valueToResource(linkedService, RestServiceLinkedService.TOKEN_ENDPOINT);
            scope = this.valueToResource(linkedService, RestServiceLinkedService.SCOPE) || undefined;
            resource = this.valueToResource(linkedService, RestServiceLinkedService.RESOURCE) || undefined;
        } else if (GlobalVariables.enableConnectorUserAuthCred && linkedService.authenticationType === restAuthTypes.UserAuth) {
            credential = linkedService.credential ?
                ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference : undefined;
        }

        properties.typeProperties = {
            url: this.valueToResource(linkedService, RestServiceLinkedService.BASE_URL),
            enableServerCertificateValidation: linkedService.enableServerCertificateValidation,
            authenticationType,
            userName,
            password,
            servicePrincipalId,
            servicePrincipalKey,
            servicePrincipalCredentialType,
            servicePrincipalEmbeddedCert,
            servicePrincipalEmbeddedCertPassword,
            tenant,
            aadResourceId,
            azureCloudType,
            clientId,
            clientSecret,
            tokenEndpoint,
            scope,
            resource,
            credential
        };

        if (!arrayIsNullOrEmpty(linkedService.authHeaders)) {
            properties.typeProperties.authHeaders = {};
            linkedService.authHeaders.forEach((pair) => {
                if (pair.value != null) {
                    properties.typeProperties.authHeaders[pair.key] = passwordOrAKVtoSecretBase(pair.value, this.context);
                }
            });
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: RestServiceLinkedService): Promise<RestServiceLinkedService> {
        const properties = linkedServiceResource.properties as Model.RestLinkedService;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const typeProperties: Model.RestLinkedServiceTypeProperties = properties.typeProperties;

        linkedService.credential = undefined;
        linkedService.authenticationType = typeProperties.authenticationType;

        linkedService.enableServerCertificateValidation = typeProperties.enableServerCertificateValidation;

        ValueAdapter.toModel(linkedService, RestServiceLinkedService.BASE_URL, typeProperties.url);
        ValueAdapter.toModel(linkedService, RestServiceLinkedService.USER_NAME, typeProperties.userName);
        ValueAdapter.toModel(linkedService, RestServiceLinkedService.SERVICE_PRINCIPAL_ID, typeProperties.servicePrincipalId);
        ValueAdapter.toModel(linkedService, RestServiceLinkedService.TENANT, typeProperties.tenant);
        ValueAdapter.toModel(linkedService, RestServiceLinkedService.AAD_RESOURCE_ID, typeProperties.aadResourceId);
        ValueAdapter.toModel(linkedService, RestServiceLinkedService.CLIENT_ID, typeProperties.clientId);
        ValueAdapter.toModel(linkedService, RestServiceLinkedService.TOKEN_ENDPOINT, typeProperties.tokenEndpoint);
        ValueAdapter.toModel(linkedService, RestServiceLinkedService.SCOPE, typeProperties.scope);
        ValueAdapter.toModel(linkedService, RestServiceLinkedService.RESOURCE, typeProperties.resource);
        ValueAdapter.toModel(linkedService, RestServiceLinkedService.AZURE_CLOUD_TYPE, typeProperties.azureCloudType);

        parameterizableSecretBaseToModel(linkedService, RestServiceLinkedService.PASSWORD, typeProperties.password, this.context,
            linkedService.authenticationType === restAuthTypes.Basic);
        parameterizableSecretBaseToModel(linkedService, RestServiceLinkedService.CLIENT_SECRET, typeProperties.clientSecret, this.context,
            linkedService.authenticationType === restAuthTypes.OAuth2ClientCredential);

        linkedService.servicePrincipalCredentialType = typeProperties.servicePrincipalCredentialType as keyof typeof servicePrincipalCredentialTypes;
        linkedService.isOldServicePrincipalResource = !linkedService.servicePrincipalCredentialType;
        if (linkedService.servicePrincipalCredentialType === servicePrincipalCredentialTypes.ServicePrincipalCert) {
            parameterizableSecretBaseToModel(linkedService, RestServiceLinkedService.SERVICE_PRINCIPAL_EMBEDDED_CERT, typeProperties.servicePrincipalEmbeddedCert, this.context,
                linkedService.authenticationType === restAuthTypes.AadServicePrincipal && !properties.typeProperties.credential);
            parameterizableSecretBaseToModel(linkedService, RestServiceLinkedService.SERVICE_PRINCIPAL_EMBEDDED_CERT_PASSWORD, typeProperties.servicePrincipalEmbeddedCertPassword, this.context,
                linkedService.authenticationType === restAuthTypes.AadServicePrincipal && !properties.typeProperties.credential);
        } else {
            parameterizableSecretBaseToModel(linkedService, RestServiceLinkedService.SERVICE_PRINCIPAL_KEY, typeProperties.servicePrincipalKey, this.context,
                linkedService.authenticationType === restAuthTypes.AadServicePrincipal && !properties.typeProperties.credential);
        }

        if (properties.typeProperties.credential) {
            linkedService.credential = ReferenceAdapter.toProxyModel(Metamodel.modelFor(Credential), properties.typeProperties.credential, this.context);
            linkedService.authReferenceMethod = restAuthRefTypes.CredentialRefTypeAuthentication;
        } else {
            linkedService.authReferenceMethod = restAuthRefTypes.InlineRefTypeAuthentication;
        }

        if (typeProperties.authHeaders) {
            linkedService.authHeaders = [];
            Object.keys(typeProperties.authHeaders).forEach((key) => {
                if (typeProperties.authHeaders[key] != null) {
                    const kv = Owned.create<KeyValuePairWithAkv>(KeyValuePairWithAkv.MODEL, linkedService);
                    kv.key = key;
                    kv.value = secretBaseToPasswordOrAKV((typeProperties.authHeaders[key]), true, this.context);
                    linkedService.authHeaders.push(kv);
                }
            });
            linkedService.authHeaderBannerMessage = '';
        } else {
            linkedService.authHeaderBannerMessage = stringResources.Rest_Auth_Header_May_Miss_Info_On_Edit;
        }
        return linkedService;
    }
}
